import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram, Facebook, Linkedin } from "lucide-react";
import { Button } from "../ui/button";
import { useContent } from "../../lib/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/impact", label: "Impact" },
  { to: "/gallery", label: "Gallery" },
  { to: "/partners", label: "Partners" },
  { to: "/donate", label: "Donate" },
  { to: "/contact", label: "Contact" },
];

const socialIcons = [
  { key: "instagram", Icon: Instagram, label: "Instagram" },
  { key: "facebook", Icon: Facebook, label: "Facebook" },
  { key: "linkedin", Icon: Linkedin, label: "LinkedIn" },
];

export const Navbar = () => {
  const { content } = useContent();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`glass-nav sticky top-0 z-40 w-full transition-shadow duration-300 ${
        scrolled ? "shadow-nav" : "shadow-[0_1px_0_rgba(226,232,240,.8)]"
      }`}
    >
      <div className={`container-tt flex items-center justify-between gap-6 transition-[padding] duration-300 ${scrolled ? "py-3" : "py-4"}`}>
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-2.5 group shrink-0">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full btn-sky-fill text-white font-display text-lg shadow-sky ring-1 ring-white/40 transition-transform duration-200 group-hover:scale-105">
            Tt
          </span>
          <span className="font-display text-xl tracking-tight text-stone">{content.brand.name}</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[15px] link-underline transition-colors ${isActive ? "text-skyDark font-medium" : "text-stone/75 hover:text-stone"}`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2">
          <div className="flex items-center gap-1 border-r border-border pr-3 mr-1">
            {socialIcons.map(({ key, Icon, label }) => (
              <a
                key={key}
                href={content.socials?.[key] || "#"}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                data-testid={`nav-social-${key}`}
                className="h-10 w-10 grid place-items-center rounded-full text-stone/60 hover:text-skyDark hover:bg-accent/60 transition-colors"
              >
                <Icon size={17} />
              </a>
            ))}
          </div>
          <Button asChild className="btn-pill btn-motion btn-sky-fill text-white h-11 px-5 text-[15px] shadow-sky hover:shadow-skyHover border-0">
            <Link to="/volunteer" data-testid="nav-cta-volunteer">Volunteer</Link>
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="xl:hidden inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white/80 text-stone transition-colors hover:border-sky/50"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 0.84, 0.44, 1] }}
            className="xl:hidden overflow-hidden border-t border-border/60 bg-white/95 backdrop-blur-lg"
          >
            <div className="container-tt py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `text-lg py-3 px-3 rounded-xl transition-colors ${isActive ? "text-skyDark bg-accent/50 font-medium" : "text-stone hover:bg-muted/70"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-2 pt-4 mt-3 border-t border-border/60">
                {socialIcons.map(({ key, Icon, label }) => (
                  <a key={key} href={content.socials?.[key] || "#"} target="_blank" rel="noreferrer" aria-label={label} className="h-11 w-11 grid place-items-center rounded-full border border-border text-stone/60 hover:text-skyDark hover:border-sky/50 transition-colors">
                    <Icon size={18} />
                  </a>
                ))}
              </div>
              <Button asChild className="btn-pill btn-motion btn-sky-fill text-white h-12 w-full text-[15px] mt-3 shadow-sky border-0">
                <Link to="/volunteer">Volunteer</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
